import React from 'react';
import { Link } from 'react-router-dom';
import {
  Tag,
  FileText,
  Brain,
  Settings,
  Newspaper,
  Users,
  BarChart3,
  Shield,
  MessageSquare,
  BookOpen,
  Bot
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const adminModules = [
    {
      name: '内容管理',
      description: '管理系统内容，包括文章、资源和学习材料',
      href: '/admin/content-management',
      icon: FileText,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600'
    },
    {
      name: '思维模型管理',
      description: '管理思维模型，包括模型创建、编辑和分类',
      href: '/admin/models',
      icon: Brain,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600'
    },
    {
      name: '学习模块管理',
      description: '管理教育内容的层级结构，包括领域、学科、知识点、文章和标签',
      href: '/admin/learning',
      icon: BookOpen,
      color: 'bg-emerald-500',
      hoverColor: 'hover:bg-emerald-600'
    },
    {
      name: '新闻洞察管理',
      description: '管理新闻源、新闻内容、分析模板和官方深度分析',
      href: '/admin/insights',
      icon: Newspaper,
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600'
    },
    {
      name: 'AI模块管理',
      description: '管理AI场景配置，包括提示词模板、模型参数和场景测试',
      href: '/admin/ai',
      icon: Bot,
      color: 'bg-violet-500',
      hoverColor: 'hover:bg-violet-600'
    },
    {
      name: '采集模块管理',
      description: '管理网页数据采集任务、会话配置、模板规则和采集数据',
      href: '/admin/scrape',
      icon: BarChart3,
      color: 'bg-cyan-500',
      hoverColor: 'hover:bg-cyan-600'
    }
  ];

  const statsCards = [
    {
      name: '总用户数',
      value: '1,234',
      change: '+12%',
      changeType: 'positive',
      icon: Users
    },
    {
      name: '活跃模型',
      value: '56',
      change: '+8%',
      changeType: 'positive',
      icon: Brain
    },
    {
      name: '训练题目',
      value: '789',
      change: '+15%',
      changeType: 'positive',
      icon: FileText
    },
    {
      name: '系统状态',
      value: '正常',
      change: '99.9%',
      changeType: 'positive',
      icon: Shield
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">管理中心</h1>
          <p className="text-gray-600">系统管理和配置中心，管理所有后台功能</p>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <Icon className="w-6 h-6 text-gray-600" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">较上月</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* 管理模块 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">管理模块</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminModules.map((module) => {
              const Icon = module.icon;
              return (
                <Link
                  key={module.name}
                  to={module.href}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200 p-6 group"
                >
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-lg ${module.color} ${module.hoverColor} transition-colors duration-200`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-4 group-hover:text-blue-600 transition-colors duration-200">
                      {module.name}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {module.description}
                  </p>
                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors duration-200">
                    进入管理
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* 快速操作 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">快速操作</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center justify-center px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-200">
              <BarChart3 className="w-5 h-5 mr-2" />
              查看系统报告
            </button>
            <button className="flex items-center justify-center px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors duration-200">
              <Users className="w-5 h-5 mr-2" />
              用户管理
            </button>
            <button className="flex items-center justify-center px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors duration-200">
              <Settings className="w-5 h-5 mr-2" />
              系统设置
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;